// =============================================================================
// ReqTable.jsx — sortable/filterable table of requests
// =============================================================================
// Renders: a table row per request with ref, shelter (optional), submit date,
//          status pill, and last-updated. Used inside both ShelterDash and
//          AdminDash. Clicking a row calls onSelect(request).
// Props:
//   requests     - array of request objects (from requests_view via portal.jsx)
//   onSelect     - row click handler
//   showShelter  - if true, include the shelter + state columns (admin/staff view)
//   repeatFlags  - optional possible-repeat map by request id (db-211, staff only): adds the
//                  ⚠ REPEAT chip under the ref and a "Repeat check" column
// =============================================================================

import React from "react";
import { useFitViewportHeight } from "../../hooks/useFitViewportHeight";
import { usePageSize } from "../../hooks/usePageSize";
import { repeatReviewState } from "../../services/repeatCheck";
import { REQUEST_STATUSES, ROLES } from "../../utils/enums";
import { fmtDateLocal, fmtDateTimeLocal } from "../../utils/helpers";
import { PageNav } from "../common/PageNav.jsx";
import { RequestRefLink } from "../common/RequestRefLink.jsx";
import { Badge } from "../form/FormUI.jsx";
import { RepeatListChip } from "../request/RepeatFlagPanel.jsx";

// Rows per page is the shared per-device preference (services/pageSize.ts).

// pinDrafts (My Requests, John 22 Jul): Drafts always sort to the TOP, then the rest
// by the active column (default Last Updated ▼ = newest action first). Off elsewhere.
// Repeat check column wording (Phase B). "Not reviewed" = confirmed since the note became
// required (24 Oct 2026) without one — the case a supervisor wants to look at.
const REPEAT_STATE_LABEL = {
  reviewed: { text: "Reviewed ✓", cls: "text-[#2E7D32] font-bold" },
  not_reviewed: { text: "Not reviewed", cls: "text-[#C62828] font-bold" },
  to_review: { text: "To review", cls: "text-[#8A4B00] font-semibold" },
  before_check: { text: "Before check", cls: "text-zephyr-muted" },
  later_match: {
    text: "Later match",
    cls: "text-zephyr-muted",
    title: "The similar request came in after this one, so no note was asked for",
  },
};

function ReqTable({ requests, onSelect, showShelter, pinDrafts, fitViewport, repeatFlags }) {
  const [REQ_PAGE_SIZE] = usePageSize();
  const { useState: useSortState } = React;
  const [sortCol, setSortCol] = useSortState("updatedAt");
  const [sortDir, setSortDir] = useSortState("desc");
  const [page, setPage] = React.useState(0);
  // fitViewport (My Requests): the rows scroll inside a box sized to the remaining
  // viewport height (measured live via useFitViewportHeight), so everything above
  // (nav, stat cards, filters, column headers) stays fixed and only the rows scroll.
  const scrollRef = React.useRef(null);
  // enabled reflects data presence so the box is (re)measured once the rows render.
  const maxH = useFitViewportHeight(scrollRef, {
    enabled: !!fitViewport && requests.length > 0,
  });
  const stickyTh = fitViewport ? " sticky top-0 z-[1] bg-zephyr-off-white" : "";
  // Back to page 1 when the list changes (filter/search/sort) so a paged view
  // doesn't strand the user on an out-of-range page.
  React.useEffect(() => setPage(0), [requests.length, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  }

  function fmt(ts) {
    if (!ts) return "-";
    return fmtDateTimeLocal(ts) || "-";
  }

  const sorted = [...requests].sort(function (a, b) {
    // Drafts pinned to the top (opt-in), then the active-column order within/after.
    if (pinDrafts) {
      const aDraft = a.status === REQUEST_STATUSES.DRAFT;
      const bDraft = b.status === REQUEST_STATUSES.DRAFT;
      if (aDraft !== bDraft) return aDraft ? -1 : 1;
    }
    let av;
    let bv;
    if (sortCol === "updatedAt") {
      av = a.updatedAt || a.date || "";
      bv = b.updatedAt || b.date || "";
    } else if (sortCol === "refNo") {
      av = a.refNo || "";
      bv = b.refNo || "";
    } else if (sortCol === "shelterName") {
      av = (a.shelterCode || a.shelterName || "").toLowerCase();
      bv = (b.shelterCode || b.shelterName || "").toLowerCase();
    } else {
      // Submitted column sorts on the Brisbane business day (reportDay).
      av = a.reportDay || a.date || "";
      bv = b.reportDay || b.date || "";
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  function SortTh({ col, label }) {
    const active = sortCol === col;
    const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⬍";
    return (
      <th
        onClick={() => toggleSort(col)}
        className={[
          "py-[10px] px-[14px] text-left text-xs font-bold border-b-2 border-zephyr-border whitespace-nowrap cursor-pointer select-none",
          active ? "text-zephyr-teal-dark" : "text-zephyr-muted",
          stickyTh,
        ].join(" ")}
      >
        {label}
        {arrow}
      </th>
    );
  }

  if (!requests.length)
    return (
      <div className="text-center py-12 px-6 text-zephyr-muted">
        <div className="text-[40px] mb-3">📋</div>
        <p>No requests found.</p>
      </div>
    );

  function PlainTh({ label }) {
    return (
      <th
        className={`py-[10px] px-[14px] text-left text-xs font-bold text-zephyr-muted border-b-2 border-zephyr-border whitespace-nowrap${stickyTh}`}
      >
        {label}
      </th>
    );
  }

  const pageCount = Math.ceil(sorted.length / REQ_PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const pageRows = sorted.slice(safePage * REQ_PAGE_SIZE, (safePage + 1) * REQ_PAGE_SIZE);

  return (
    <>
      <div
        ref={scrollRef}
        className="overflow-x-auto"
        style={fitViewport && maxH ? { maxHeight: maxH, overflowY: "auto" } : undefined}
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-zephyr-off-white">
              <SortTh col="refNo" label="Reference No." />
              {showShelter && (
                <>
                  <SortTh col="shelterName" label="DFV Service" />
                  <PlainTh label="State" />
                </>
              )}
              <PlainTh label="Ref" />
              <SortTh col="date" label="Submitted" />
              <PlainTh label="Children" />
              <PlainTh label="Status" />
              <SortTh col="updatedAt" label="Last Updated" />
              <PlainTh label="Updates" />
              {repeatFlags && <PlainTh label="Repeat check" />}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr
                key={r.id}
                onClick={() => onSelect(r)}
                className={`${i % 2 === 0 ? "bg-white" : "bg-zephyr-off-white"} cursor-pointer`}
              >
                <td className="py-3 px-[14px]">
                  <div className="flex flex-col">
                    <RequestRefLink
                      href={`/requests/${r.id}`}
                      refNo={r.refNo}
                      onOpen={() => onSelect(r)}
                      className="font-bold text-zephyr-teal underline"
                    />
                    {repeatFlags && <RepeatListChip flag={repeatFlags[String(r.id)]} />}
                  </div>
                </td>
                {showShelter && (
                  <>
                    <td className="py-3 px-[14px]">
                      {r.shelterCode && (
                        <span className="font-bold text-zephyr-teal-dark">{r.shelterCode}</span>
                      )}
                      {r.shelterCode && r.shelterName && (
                        <span className="text-zephyr-muted text-xs"> — {r.shelterName}</span>
                      )}
                      {!r.shelterCode && r.shelterName}
                    </td>
                    <td className="py-3 px-[14px]">
                      <span className="bg-zephyr-teal-light text-zephyr-teal-dark rounded-[4px] py-0.5 px-2 text-xs font-semibold">
                        {r.state}
                      </span>
                    </td>
                  </>
                )}
                <td className="py-3 px-[14px] text-zephyr-muted max-w-[90px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {r.shelterRef}
                </td>
                <td className="py-3 px-[14px] text-zephyr-muted">
                  {r.createdAt
                    ? fmtDateTimeLocal(r.createdAt)
                    : r.date
                      ? fmtDateLocal(r.date)
                      : "-"}
                </td>
                <td className="py-3 px-[14px] text-center">
                  {r.requestType === "stock_top_up" ? (
                    <span className="inline-block px-2 py-[1px] rounded-[10px] text-[11px] font-bold bg-[#E0F2F1] text-[#00695C] border border-[#B2DFDB]">
                      Top-up
                    </span>
                  ) : (
                    (r.children || []).length || "-"
                  )}
                </td>
                <td className="py-3 px-[14px]">
                  <Badge status={r.status} />
                </td>
                <td className="py-3 px-[14px] text-xs text-zephyr-text">{fmt(r.updatedAt)}</td>
                <td className="py-3 px-[14px] text-xs">
                  {r.updatedByRole === ROLES.STAFF ? (
                    <span className="bg-zephyr-blue-light text-zephyr-blue rounded-[4px] py-0.5 px-2 text-[11px] font-bold">
                      Updated by Zephyr
                    </span>
                  ) : r.updatedByRole === ROLES.SHELTER ? (
                    // `updated_by_role` records WHICH SIDE wrote last, and the
                    // "you" wording is the DFV Service's own point of view (its
                    // My-Requests list). This same table is reused on the staff
                    // All-Requests view (showShelter), where "you" would read as
                    // the responder — so there it says "Updated by Service".
                    <span className="text-zephyr-muted text-xs">
                      {showShelter ? "Updated by Service" : "Updated by you"}
                    </span>
                  ) : (
                    <span className="text-zephyr-muted">—</span>
                  )}
                </td>
                {repeatFlags && (
                  <td className="py-3 px-[14px] text-xs whitespace-nowrap">
                    {(function () {
                      const st = repeatReviewState(
                        repeatFlags[String(r.id)],
                        r.approvalsConfirmedAt
                      );
                      const lab = REPEAT_STATE_LABEL[st];
                      return lab ? (
                        <span className={lab.cls} title={lab.title}>
                          {lab.text}
                        </span>
                      ) : (
                        <span className="text-zephyr-muted">—</span>
                      );
                    })()}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageNav page={safePage} pageCount={pageCount} onPage={setPage} />
    </>
  );
}

export { ReqTable };
